import { NavLink, LoaderFunction, useLoaderData, Link } from 'react-router-dom';
import { HomeIcon } from '@heroicons/react/solid';
import { paths } from '@mosey/api-types';
import { PageHeader } from '@mosey/components/page-header/PageHeader';
import { PageTitle } from '@mosey/components/page-header/PageTitle';
import { Button } from '@mosey/components/buttons/Button';
import { Table } from '@mosey/components/table/Table';
import { HeaderRow } from '@mosey/components/table/HeaderRow';
import { HeaderCell } from '@mosey/components/table/HeaderCell';
import { TableBody } from '@mosey/components/table/TableBody';
import { TableRow } from '@mosey/components/table/TableRow';
import { TableCell } from '@mosey/components/table/TableCell';
import { TextLink } from '@mosey/components/navigation/TextLink';
import { USMap, Location as USMapLocation } from '../../components';
import { apiBatch } from '../../utils/fetchApi';
import { RegionStatus } from '../../types';
import { useLegalEntityRegions, useUser } from '../../hooks/useUser';
import { getTasksAPIEndDate } from '../tasks-framework/utils';
import { YesNoIndicator } from './YesNoIndicator';
import {
  LightningBoltListIcon,
  QuestionListIcon,
  StopwatchIcon,
} from '@mosey/components/Icons';
import clsx from 'clsx';
import { APP_BASE_URL } from '../../settings/config';
import { TextWithDotIndicator } from './TextWithDotIndicator';

type LocationsConfigurationResponse =
  paths['/api/legal_entity/locations/overview']['get']['responses'][200]['content']['application/json'];
type LocationsSummary =
  paths['/api/legal_entity/locations/summary_v2']['get']['responses']['200']['content']['application/json'];

export const loader: LoaderFunction = async () => {
  const endDate = getTasksAPIEndDate();

  return apiBatch({
    configuration: {
      url: '/api/legal_entity/locations/overview',
      method: 'GET',
    },
    summary: {
      url: `/api/legal_entity/locations/summary_v2?end_date=${endDate}`,
      method: 'GET',
    },
  });
};

export const Component = () => {
  const {
    legal_entity: { incorporation_region: incorporationRegion },
  } = useUser();
  const {
    configuration,
    summary: {
      question_tasks: questionTasks,
      requirement_tasks: requirementTasks,
    },
  } = useLoaderData() as {
    configuration: LocationsConfigurationResponse;
    summary: LocationsSummary;
  };
  const states = useLegalEntityRegions();
  const activeStates = states.filter(
    (state) => state.status !== RegionStatus.Exiting,
  );
  const USMapStates = activeStates.map((state) => state.region);
  // For now we don't have city level data so this is empty
  const USMapLocations: USMapLocation[] = [];
  const overdueStates = activeStates.filter((state) => {
    const requirementTasksForState = requirementTasks[state.code];

    if (requirementTasksForState) {
      return requirementTasksForState.overdue_count.todo > 0;
    }

    return false;
  });

  const actionableStates = activeStates.filter((state) => {
    const questionTasksForState = questionTasks[state.code];
    const requirementTasksForState = requirementTasks[state.code];
    let todoCount = 0;

    if (questionTasksForState) {
      todoCount = Object.values(questionTasksForState).reduce((acc, next) => {
        if (typeof next !== 'boolean') {
          return acc + next.todo;
        }

        return acc;
      }, 0);
    }

    if (requirementTasksForState) {
      todoCount += Object.values(requirementTasksForState).reduce(
        (acc, next) => {
          if (typeof next !== 'boolean') {
            return acc + next.todo;
          }

          return acc;
        },
        0,
      );
    }

    return todoCount > 0;
  });

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <PageHeader>
        <PageTitle>Locations</PageTitle>

        <div className="ml-auto">
          <Button as={NavLink} to="/locations/usa/setup" size="large">
            Add new state
          </Button>
        </div>
      </PageHeader>

      <div className="relative flex grow flex-col gap-y-6 overflow-auto px-8 pt-6">
        <div className="relative flex items-center justify-center rounded-md border border-gray-200 bg-teal-100">
          <USMap
            activeStates={USMapStates}
            overdueStates={overdueStates}
            actionableStates={actionableStates}
            locations={USMapLocations}
            className="max-h-72"
          />
        </div>

        <div className="mb-6 text-sm text-zinc-700">
          <div className="rounded-md border">
            <Table border={false}>
              <HeaderRow>
                <HeaderCell scope="col" wrap>
                  State
                </HeaderCell>
                <HeaderCell scope="col" wrap>
                  Employees
                </HeaderCell>
                <HeaderCell scope="col" wrap>
                  &ldquo;Doing Business&rdquo;
                </HeaderCell>
                <HeaderCell>Registered Agent</HeaderCell>
                <HeaderCell scope="col" wrap>
                  Business Income Tax Nexus
                </HeaderCell>
                <HeaderCell scope="col" wrap>
                  Sales Tax Nexus
                </HeaderCell>
              </HeaderRow>

              <TableBody>
                {activeStates.map((state) => {
                  const questionTasksForState = questionTasks[state.code];
                  const requirementTasksForState = requirementTasks[state.code];

                  const isAssessment = questionTasksForState
                    ? questionTasksForState.setup_count.todo > 0
                    : false;
                  const isSetup = requirementTasksForState
                    ? requirementTasks[state.code].setup_count.todo > 0
                    : false;
                  const hasOverdueTasks = requirementTasksForState
                    ? requirementTasksForState.overdue_count.todo > 0
                    : false;
                  const isIncorporationRegion =
                    incorporationRegion?.id === state.code;

                  let statusText;
                  let Icon;

                  if (hasOverdueTasks) {
                    Icon = StopwatchIcon;
                    statusText = 'Overdue tasks';
                  } else if (isSetup) {
                    Icon = LightningBoltListIcon;
                    statusText = 'setup tasks';
                  } else if (isAssessment) {
                    Icon = QuestionListIcon;
                    statusText = 'assessment tasks';
                  } else if (isIncorporationRegion) {
                    Icon = HomeIcon;
                    statusText = 'Incorporation State';
                  }

                  const datums = configuration[state.code]?.datums ?? {};
                  const registeredAgentStatus =
                    configuration[state.code]?.registered_agent_status;

                  return (
                    <TableRow key={state.region.id} align="middle">
                      <HeaderCell scope="row" variant="row">
                        <TextLink to={state.code.toLowerCase()}>
                          <div className="flex items-center gap-x-1 font-semibold">
                            {state.name}

                            {Icon && (
                              <Icon
                                className={clsx(
                                  'shrink-0',
                                  isIncorporationRegion ? 'size-3' : 'size-4',
                                  hasOverdueTasks
                                    ? 'text-rose-900'
                                    : 'text-teal-800',
                                )}
                                aria-label={statusText}
                              />
                            )}
                          </div>
                        </TextLink>
                      </HeaderCell>
                      <TableCell>{state.employee_count}</TableCell>
                      <TableCell>
                        <YesNoIndicator
                          value={
                            isIncorporationRegion
                              ? 'true'
                              : datums && datums['doing-business']
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <>
                          {registeredAgentStatus === 'transferable' ? (
                            <Button
                              size="small"
                              variant="secondary"
                              as={Link}
                              to={`/automation/registered-agent-transfer/${state.region.id}?callback_url=${APP_BASE_URL}/locations/usa`}
                              aria-label={`Transfer registered agent for ${state.region.name}`}
                            >
                              Transfer
                            </Button>
                          ) : (
                            <TextWithDotIndicator
                              indicator={
                                registeredAgentStatus === 'in-progress'
                                  ? 'amber'
                                  : undefined
                              }
                            >
                              {registeredAgentStatus === 'done'
                                ? 'Mosey'
                                : registeredAgentStatus === 'in-progress'
                                  ? 'Transferring'
                                  : undefined}
                            </TextWithDotIndicator>
                          )}
                        </>
                      </TableCell>
                      <TableCell>
                        <YesNoIndicator
                          value={datums && datums['business-income-tax-nexus']}
                        />
                      </TableCell>
                      <TableCell>
                        <YesNoIndicator
                          value={datums && datums['sales-tax-nexus']}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};
